/**
 * Accept Invitation Page
 *
 * Entry point for federated user invitation acceptance flow.
 * Uses MSAL to authenticate against the federated tenant.
 *
 * Flow:
 * 1. User clicks invitation link in email
 * 2. This page extracts the token from URL
 * 3. Calls backend /api/auth/accept-invitation to get org info
 * 4. Uses MSAL to authenticate against the federated tenant (PKCE flow)
 * 5. Extracts claims from id_token
 * 6. Calls /api/auth/complete-invitation with claims to create user
 * 7. Shows success message and redirects to login
 */

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import {
  Button,
  Spinner,
  Text,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import {
  CheckmarkCircle24Filled,
  ErrorCircle24Filled,
} from '@fluentui/react-icons';
import { useMsal } from '@azure/msal-react';
import { config } from '../config';
import { useAuth, LAST_USER_EMAIL_KEY } from '../contexts/AuthContext';

// Microsoft Logo SVG component
const MicrosoftLogo = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 21 21" style={{ width: 20, height: 20 }}>
    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
  </svg>
);

const useStyles = makeStyles({
  // Full-page container with gradient mesh background
  page: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    padding: tokens.spacingVerticalXXL,
    boxSizing: 'border-box',
    backgroundColor: tokens.colorNeutralBackground3,
    position: 'relative',
    overflow: 'hidden',
  },

  // Animated gradient mesh
  gradientMesh: {
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: `
      radial-gradient(ellipse at 20% 20%, color-mix(in srgb, ${tokens.colorBrandBackground} 12%, transparent) 0%, transparent 50%),
      radial-gradient(ellipse at 80% 80%, color-mix(in srgb, ${tokens.colorBrandBackground} 8%, transparent) 0%, transparent 50%),
      radial-gradient(ellipse at 40% 70%, color-mix(in srgb, ${tokens.colorBrandBackground} 6%, transparent) 0%, transparent 40%)`,
    animationName: {
      '0%, 100%': { transform: 'translate(0, 0) rotate(0deg)' },
      '33%': { transform: 'translate(2%, 1%) rotate(1deg)' },
      '66%': { transform: 'translate(-1%, 2%) rotate(-1deg)' },
    },
    animationDuration: '20s',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
    pointerEvents: 'none',
  },

  // Subtle grid pattern overlay
  gridPattern: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `
      linear-gradient(${tokens.colorNeutralStroke2} 1px, transparent 1px),
      linear-gradient(90deg, ${tokens.colorNeutralStroke2} 1px, transparent 1px)`,
    backgroundSize: '60px 60px',
    opacity: 0.3,
    pointerEvents: 'none',
  },

  // Main card container
  card: {
    width: '100%',
    maxWidth: '480px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '16px',
    boxShadow: `
      0 4px 6px -1px rgba(0, 0, 0, 0.1),
      0 20px 40px -10px rgba(0, 0, 0, 0.15),
      0 0 0 1px ${tokens.colorNeutralStroke2}`,
    overflow: 'hidden',
    position: 'relative',
    zIndex: 1,
    animationName: {
      from: {
        opacity: 0,
        transform: 'translateY(8px)',
      },
      to: {
        opacity: 1,
        transform: 'translateY(0)',
      },
    },
    animationDuration: '0.4s',
    animationTimingFunction: 'ease-out',
    animationFillMode: 'both',
  },

  // Hero section for logo
  logoHero: {
    backgroundColor: tokens.colorNeutralBackground1,
    padding: '40px 48px 16px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },

  logo: {
    maxWidth: '240px',
    height: 'auto',
  },

  // Content area below hero
  content: {
    padding: '32px 48px 48px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalL,
    textAlign: 'center',
  },

  // Header with icon and title
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalM,
  },

  errorIcon: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: '32px',
  },

  successIcon: {
    color: tokens.colorPaletteGreenForeground1,
    fontSize: '32px',
  },

  // Subtitle text
  subtitle: {
    color: tokens.colorNeutralForeground2,
    lineHeight: tokens.lineHeightBase400,
  },

  // Organization info box
  orgInfo: {
    backgroundColor: tokens.colorNeutralBackground3,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderRadius: tokens.borderRadiusMedium,
    width: '100%',
    textAlign: 'center',
  },

  // Sign in button
  signInButton: {
    width: '100%',
    height: '52px',
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    borderRadius: '10px',
    transition: 'all 0.2s ease',
    ':hover': {
      transform: 'translateY(-1px)',
      boxShadow: tokens.shadow8,
    },
    ':active': {
      transform: 'translateY(0)',
    },
  },

  // Microsoft button style (white background with stronger shadow)
  microsoftButton: {
    backgroundColor: '#ffffff',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    color: '#1e293b',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 1px 3px rgba(0, 0, 0, 0.08)',
    ':hover': {
      backgroundColor: '#f8fafc',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.18), 0 2px 6px rgba(0, 0, 0, 0.1)',
    },
  },

  // Button content layout
  buttonContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalS,
  },

  // Email highlight
  emailHighlight: {
    color: tokens.colorBrandForeground2,
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },

  link: {
    color: tokens.colorBrandForeground1,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },

  // Footer section
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: tokens.spacingVerticalL,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    width: '100%',
  },

  footerText: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
});

interface OrgInfo {
  organization_id: number;
  organization_name: string;
  azure_tenant_id: string;
  invitation_email: string;
  invitation_name: string;
  invitation_token: string;
}

interface CompleteInvitationResponse {
  success: boolean;
  user: {
    id: number;
    email: string;
    name: string;
    organization_id: number;
    role: string;
  };
  message: string;
}

type FlowState =
  | 'loading'
  | 'validating'
  | 'ready_to_auth'
  | 'authenticating'
  | 'completing'
  | 'success'
  | 'error';

export function AcceptInvitationPage(): JSX.Element {
  const styles = useStyles();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { instance } = useMsal();
  const { login } = useAuth();

  const [flowState, setFlowState] = useState<FlowState>('loading');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orgInfo, setOrgInfo] = useState<OrgInfo | null>(null);
  const [createdUser, setCreatedUser] =
    useState<CompleteInvitationResponse['user'] | null>(null);

  const token = searchParams.get('token');

  // Step 1: Validate invitation and get org info
  useEffect(() => {
    if (!token) {
      setError('No invitation token provided');
      setFlowState('error');
      return;
    }

    const validateInvitation = async () => {
      setFlowState('validating');
      try {
        const response = await fetch(
          `${config.backendUrl}/api/auth/accept-invitation?token=${encodeURIComponent(token)}`
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Invalid or expired invitation');
        }

        const data: OrgInfo = await response.json();
        setOrgInfo(data);
        setFlowState('ready_to_auth');
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to validate invitation'
        );
        setFlowState('error');
      }
    };

    validateInvitation();
  }, [token]);

  // Step 2: Authenticate with MSAL
  const handleAuthenticate = useCallback(async () => {
    if (!orgInfo) return;

    setFlowState('authenticating');
    try {
      // Authenticate against the specific federated tenant
      const result = await instance.loginPopup({
        scopes: ['openid', 'profile', 'email'],
        authority: `https://login.microsoftonline.com/${orgInfo.azure_tenant_id}`,
        prompt: 'select_account',
        loginHint: orgInfo.invitation_email,
      });

      // Extract claims from the account
      const account = result.account;
      if (!account) {
        throw new Error('No account returned from authentication');
      }

      // Get claims from ID token or account info
      const claims = {
        oid: account.localAccountId,
        email:
          account.username ||
          (account.idTokenClaims as Record<string, unknown> | undefined)?.[
            'email'
          ] ||
          orgInfo.invitation_email,
        preferred_username: account.username,
        name:
          account.name ||
          (account.idTokenClaims as Record<string, unknown> | undefined)?.[
            'name'
          ] ||
          orgInfo.invitation_name,
        tid: account.tenantId,
      };

      // Step 3: Complete invitation with claims
      setFlowState('completing');
      const completeResponse = await fetch(
        `${config.backendUrl}/api/auth/complete-invitation`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            invitation_token: orgInfo.invitation_token,
            claims,
          }),
        }
      );

      if (!completeResponse.ok) {
        const errorData = await completeResponse.json();
        throw new Error(
          errorData.detail || 'Failed to complete invitation acceptance'
        );
      }

      const completeData: CompleteInvitationResponse =
        await completeResponse.json();
      setCreatedUser(completeData.user);
      setFlowState('success');

      // Clear the MSAL cache for this federated account
      // (user will login fresh with main app MSAL instance)
      await instance.logout({
        account: account,
        onRedirectNavigate: () => false, // Prevent redirect
      });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Authentication failed. Please try again.'
      );
      setFlowState('error');
    }
  }, [instance, orgInfo]);

  // Error state
  if (flowState === 'error') {
    return (
      <div className={styles.page}>
        <div className={styles.gradientMesh} aria-hidden="true" />
        <div className={styles.gridPattern} aria-hidden="true" />
        <div className={styles.card}>
          <div className={styles.logoHero}>
            <img src="/irpilot-logo.svg" alt="IRPilot" className={styles.logo} />
          </div>
          <div className={styles.content}>
            <div className={styles.header}>
              <ErrorCircle24Filled className={styles.errorIcon} />
              <Text size={500} weight="semibold">Invitation Error</Text>
            </div>
            <Text className={styles.subtitle}>{error}</Text>
            <Text className={styles.subtitle}>
              Please contact your organization administrator for a new
              invitation.
            </Text>
            <Link to="/login" className={styles.link}>
              Go to Login
            </Link>
            <div className={styles.footer}>
              <Text className={styles.footerText}>
                Protected by Microsoft Entra ID
              </Text>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle login from success screen
  const handleContinueToApp = async () => {
    if (!createdUser) return;

    setIsLoggingIn(true);
    // Store email for login hint
    localStorage.setItem(LAST_USER_EMAIL_KEY, createdUser.email);

    try {
      await login(createdUser.email);
      // Navigate to app after successful login
      navigate('/app', { replace: true });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Login failed:', error);
      // Fall back to login page if something goes wrong
      navigate('/login');
    } finally {
      setIsLoggingIn(false);
    }
  };

  // Success state
  if (flowState === 'success' && createdUser) {
    return (
      <div className={styles.page}>
        <div className={styles.gradientMesh} aria-hidden="true" />
        <div className={styles.gridPattern} aria-hidden="true" />
        <div className={styles.card}>
          <div className={styles.logoHero}>
            <img src="/irpilot-logo.svg" alt="IRPilot" className={styles.logo} />
          </div>
          <div className={styles.content}>
            <div className={styles.header}>
              <CheckmarkCircle24Filled className={styles.successIcon} />
              <Text size={500} weight="semibold">Welcome to IRPilot!</Text>
            </div>
            <Text className={styles.subtitle}>
              Your account has been created successfully.
            </Text>
            <div className={styles.orgInfo}>
              <Text size={400} weight="semibold">{createdUser.name}</Text>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Text className={styles.subtitle}>You can now access the platform as</Text>
              <br />
              <Text className={styles.emailHighlight}>{createdUser.email}</Text>
            </div>
            <Button
              appearance="secondary"
              size="large"
              className={mergeClasses(styles.signInButton, styles.microsoftButton)}
              onClick={handleContinueToApp}
              disabled={isLoggingIn}
            >
              <span className={styles.buttonContent}>
                {isLoggingIn ? (
                  <>
                    <Spinner size="tiny" />
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <MicrosoftLogo />
                    <span>Continue to IRPilot</span>
                  </>
                )}
              </span>
            </Button>
            <div className={styles.footer}>
              <Text className={styles.footerText}>
                Protected by Microsoft Entra ID
              </Text>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Ready to authenticate state
  if (flowState === 'ready_to_auth' && orgInfo) {
    return (
      <div className={styles.page}>
        <div className={styles.gradientMesh} aria-hidden="true" />
        <div className={styles.gridPattern} aria-hidden="true" />
        <div className={styles.card}>
          <div className={styles.logoHero}>
            <img src="/irpilot-logo.svg" alt="IRPilot" className={styles.logo} />
          </div>
          <div className={styles.content}>
            <Text size={500} weight="semibold">Accept Invitation</Text>
            <Text className={styles.subtitle}>
              You&apos;ve been invited to join the{' '}
              <Text weight="semibold">{orgInfo.organization_name}</Text> organization.
            </Text>
            <div className={styles.orgInfo}>
              <Text size={200}>Invitation for:</Text>
              <br />
              <Text size={500} weight="semibold">{orgInfo.invitation_name}</Text>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Text className={styles.subtitle}>Sign in as</Text>
              <div style={{ marginTop: tokens.spacingVerticalM, marginBottom: tokens.spacingVerticalM }}>
                <Text className={styles.emailHighlight}>{orgInfo.invitation_email}</Text>
              </div>
              <Text className={styles.subtitle}>to complete your registration.</Text>
            </div>
            <Button
              appearance="secondary"
              size="large"
              className={mergeClasses(styles.signInButton, styles.microsoftButton)}
              onClick={handleAuthenticate}
            >
              <span className={styles.buttonContent}>
                <MicrosoftLogo />
                <span>Sign in with Microsoft</span>
              </span>
            </Button>
            <div className={styles.footer}>
              <Text className={styles.footerText}>
                Protected by Microsoft Entra ID
              </Text>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading/validating/authenticating/completing states
  const statusMessages: Record<FlowState, string> = {
    loading: 'Loading...',
    validating: 'Validating invitation...',
    ready_to_auth: 'Ready to authenticate',
    authenticating: 'Signing in with your corporate account...',
    completing: 'Setting up your account...',
    success: 'Account created!',
    error: 'An error occurred',
  };

  return (
    <div className={styles.page}>
      <div className={styles.gradientMesh} aria-hidden="true" />
      <div className={styles.gridPattern} aria-hidden="true" />
      <div className={styles.card}>
        <div className={styles.logoHero}>
          <img src="/irpilot-logo.svg" alt="IRPilot" className={styles.logo} />
        </div>
        <div className={styles.content}>
          <Spinner size="large" />
          <Text size={400}>{statusMessages[flowState]}</Text>
          {flowState === 'authenticating' && (
            <Text size={200} className={styles.subtitle}>
              A sign-in window should open. If not, please check your popup
              blocker.
            </Text>
          )}
        </div>
      </div>
    </div>
  );
}
